import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu';
import styled, { css } from 'styled-components';

import Text from 'components/core/typography/Text';
import { ChevronRightIcon } from 'components/ui/icons';
import { ListItem, ListSectionItemTitle } from 'components/ui/lists/CommonList';

import { DIVIDER } from 'styles/color';
import { SECTION_PADDING } from 'styles/spacing';
import { FONT_WEIGHT_MEDIUM, NEUTRAL_0, NEUTRAL_050, SPACE_700 } from 'styles/tokens';

const ITEM_PADDING = SECTION_PADDING;

/**
 * The trailing chevron every item gets by default. An explicit `styled()` rather than a `css` prop on
 * the icon: the `css` prop compiles to a wrapper declared below its use site, so a module-scope
 * element built with one gets a component type of `undefined`.
 *
 * The gap sets the row's min-content width rather than a visible offset — the title is
 * `space-between`, so the chevron already sits flush right. It matches `ListItem`'s own default, so
 * an in-menu row and a standalone one stay the same width.
 */
export const DefaultItemIcon = styled(ChevronRightIcon)`
  margin-left: ${SPACE_700};
`;

const dimmedWhenDisabled = css`
  pointer-events: none;

  ${Text} {
    opacity: 0.3;
  }

  svg {
    display: none;
  }
`;

/**
 * The item as rendered outside a menu, where there is no Radix `Root` to attach to and `ListItem`'s
 * own markup — fill button included — is still the right thing.
 */
export const StandaloneMenuItem = styled(ListItem)<{ disabled?: boolean }>`
  background-color: ${NEUTRAL_0};
  display: block;
  padding: ${ITEM_PADDING};

  > ${ListSectionItemTitle} {
    font-weight: ${FONT_WEIGHT_MEDIUM};
  }
  ${({ disabled }) => disabled && dimmedWhenDisabled}
`;

/**
 * The item as rendered inside an open menu.
 *
 * Radix owns the element, so this only adds a class to it — no `asChild`, which could not wrap an
 * override that forwards no ref or renders `null`. It reproduces `ListSectionItem`'s box rather than
 * composing through `ListItem`, because `ListItem` nests a real `<button>` that would break the
 * roving focus this element exists to carry.
 */
export const InMenuItem = styled(RadixDropdownMenu.Item)<{
  /**
   * Let a full-row override own its inset.
   */
  $hasOverride: boolean;
}>`
  background-color: ${NEUTRAL_0};

  /* The standalone variant gets its pointer from the fill button; Radix's div has to declare its own
   * or the label shows the text-selection cursor. */
  cursor: pointer;

  /* Radix moves DOM focus onto the highlighted item; the background is the affordance instead of a
   * focus ring, so hover and keyboard highlight read identically. */
  outline: none;
  padding: ${({ $hasOverride }) => ($hasOverride ? 0 : ITEM_PADDING)};
  position: relative;

  /* A menu row reads as a control, not text, so a click-drag over the label should highlight the
   * row (Radix's own affordance) rather than select the label text underneath it. */
  user-select: none;
  width: 100%;

  /* An override is free to bow out by rendering nothing; without this the row's own padding and
   * border stay behind as a blank, arrow-focusable stop. Also takes it out of Radix's roving focus,
   * since an unrendered element cannot receive DOM focus. */
  &:empty {
    display: none;
  }

  /* Only a row with a visible sibling below it needs a divider, so a trailing :empty row (which
   * :last-child still matches structurally despite its display: none) cannot leave one stranded at
   * the menu's bottom edge. */
  &:not(:empty):has(~ *:not(:empty)) {
    border-bottom: 1px solid ${DIVIDER};
  }

  &:hover,
  &[data-highlighted] {
    background: ${NEUTRAL_050};
  }

  > ${ListSectionItemTitle} {
    font-weight: ${FONT_WEIGHT_MEDIUM};

    /* The standalone variant blocks pointer events here so clicks reach the fill button underneath.
     * There is no fill button in a menu, so let them through to the item itself. */
    pointer-events: auto;
  }

  &[data-disabled] {
    ${dimmedWhenDisabled}
  }
`;
